/**
 * Library list rows and the delete dialog (split out of LibraryScreen).
 */
import React from 'react';
import { ActivityIndicator, TouchableOpacity, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { Text } from '@/components/ui/text';
import { reviewDesk } from '@/design-system';
import type { UserDocumentItem } from '@/services/api';
import type { SharedDocument } from '@/types';
import { useTranslation } from '@/hooks/useTranslation';
import { libraryItemStatus } from '../libraryDocuments';
import { COLORS, formatDate, formatDuration, formatFileSize, isFailedTranscript } from '../libraryFormat';
import { webStyles } from '../libraryStyles';

type TFn = (key: string, defaultOrVars?: any, vars?: any) => string;
type LibraryItem = UserDocumentItem | SharedDocument;

function itemIcon(item: LibraryItem) {
  if (item.document_type === 'user_transcript') return 'music-note' as const;
  if (item.document_type === 'user_material') return 'attach-file' as const;
  return 'description' as const;
}

function itemMeta(item: LibraryItem, t: TFn) {
  const isVoice = item.document_type === 'user_transcript';
  const size = 'file_size' in item ? item.file_size : undefined;
  const date = 'created_at' in item ? item.created_at : item.shared_at;
  return [
    isVoice ? t('library.typeVoice') : t('library.typeText'),
    isVoice && item.audio_duration_seconds ? formatDuration(item.audio_duration_seconds) : undefined,
    !isVoice && size ? formatFileSize(size) : undefined,
    formatDate(date, t),
  ].filter(Boolean).join('  ·  ');
}

/** Row status pill (CHAT-9: a finished transcript is "Transcribed", not "Analyzed"). */
function itemStatus(item: LibraryItem, t: TFn) {
  switch (libraryItemStatus(item)) {
    case 'material':
      return { label: t('library.filterMaterials'), tone: 'neutral' as const, icon: 'attach-file' as const };
    case 'processing':
      return { label: t('library.processing'), tone: 'warning' as const, icon: 'schedule' as const };
    case 'failed':
      return { label: t('common.failed'), tone: 'error' as const, icon: 'error-outline' as const };
    case 'analyzed':
      return { label: t('library.analyzed'), tone: 'success' as const, icon: 'check-circle' as const };
    case 'transcribed':
      return { label: t('chatLibrary.library.transcribed'), tone: 'neutral' as const, icon: 'graphic-eq' as const };
    default:
      return { label: t('library.notAnalyzed'), tone: 'neutral' as const, icon: 'radio-button-unchecked' as const };
  }
}

function StatusPill({ item, isPhone, colored }: { item: LibraryItem; isPhone: boolean; colored: boolean }) {
  const { t } = useTranslation();
  const status = itemStatus(item, t);
  const iconColor = !colored
    ? reviewDesk.muted
    : status.tone === 'success' ? reviewDesk.success : status.tone === 'warning' ? reviewDesk.amber : reviewDesk.muted;
  return (
    <View style={[
      webStyles.statusPill,
      isPhone && webStyles.statusPillTiny,
      isPhone && webStyles.statusPillPhone,
      (webStyles as any)[`statusPill_${status.tone}`],
    ]}>
      <MaterialIcons name={status.icon} size={15} color={iconColor} />
      <Text style={[webStyles.statusText, (webStyles as any)[`statusText_${status.tone}`]]}>{status.label}</Text>
    </View>
  );
}

export interface LibraryDocumentRowProps {
  item: UserDocumentItem;
  isPhone: boolean;
  canShare: boolean;
  deleting: boolean;
  onOpen: (item: UserDocumentItem) => void;
  onDelete: (item: UserDocumentItem) => void;
  onShare: (item: UserDocumentItem) => void;
  onManageSharing: (item: UserDocumentItem) => void;
}

export function LibraryDocumentRow({
  item,
  isPhone,
  canShare,
  deleting,
  onOpen,
  onDelete,
  onShare,
  onManageSharing,
}: LibraryDocumentRowProps) {
  const { t } = useTranslation();
  const failed = isFailedTranscript(item);
  return (
    <TouchableOpacity
      testID={`library_document_${item.id}`}
      style={[
        webStyles.sessionRow,
        isPhone && webStyles.sessionRowTiny,
        isPhone && webStyles.sessionRowPhone,
        failed && webStyles.sessionRowFailed,
      ]}
      onPress={() => (failed ? onDelete(item) : onOpen(item))}
      accessibilityRole="button"
    >
      <View style={webStyles.fileIcon}>
        <MaterialIcons name={itemIcon(item)} size={26} color={reviewDesk.accent} />
      </View>
      <View style={[webStyles.sessionInfo, isPhone && webStyles.sessionInfoPhone]}>
        <Text style={webStyles.sessionTitle} numberOfLines={2}>{item.original_filename}</Text>
        <Text style={webStyles.sessionMeta}>{itemMeta(item, t)}</Text>
      </View>
      <StatusPill item={item} isPhone={isPhone} colored />
      {canShare && !failed && (
        <View style={[
          webStyles.shareActions,
          isPhone && webStyles.shareActionsTiny,
          isPhone && webStyles.shareActionsPhone,
        ]}>
          <TouchableOpacity
            onPress={(e) => {
              e.stopPropagation?.();
              onShare(item);
            }}
            style={webStyles.shareActionButton}
            accessibilityRole="button"
            accessibilityLabel={t('library.shareWithMentor')}
          >
            <MaterialIcons name="ios-share" size={17} color={reviewDesk.accent} />
            <Text style={webStyles.shareActionText}>{t('library.shareWithMentor')}</Text>
          </TouchableOpacity>
          <TouchableOpacity
            onPress={(e) => {
              e.stopPropagation?.();
              onManageSharing(item);
            }}
            style={webStyles.manageActionButton}
            accessibilityRole="button"
            accessibilityLabel={t('library.manageSharing')}
          >
            <MaterialIcons name="manage-accounts" size={17} color={reviewDesk.inkSoft} />
            <Text style={webStyles.manageActionText}>{t('library.manageSharing')}</Text>
          </TouchableOpacity>
        </View>
      )}
      {failed ? (
        <TouchableOpacity
          onPress={(e) => {
            e.stopPropagation?.();
            onDelete(item);
          }}
          style={[
            webStyles.deleteActionButton,
            isPhone && webStyles.deleteActionButtonTiny,
            isPhone && webStyles.rowActionPhone,
          ]}
          accessibilityRole="button"
          accessibilityLabel={t('library.deleteFailedArtifactAction')}
        >
          {deleting ? (
            <ActivityIndicator size="small" color={COLORS.error} />
          ) : (
            <>
              <MaterialIcons name="delete-outline" size={17} color={COLORS.error} />
              <Text style={webStyles.deleteActionText}>{t('library.deleteFailedArtifactAction')}</Text>
            </>
          )}
        </TouchableOpacity>
      ) : (
        <>
          <TouchableOpacity
            onPress={(e) => {
              e.stopPropagation?.();
              onDelete(item);
            }}
            style={webStyles.iconButton}
            accessibilityRole="button"
            accessibilityLabel={t('common.delete')}
          >
            {deleting ? (
              <ActivityIndicator size="small" color={reviewDesk.muted} />
            ) : (
              <MaterialIcons name="delete-outline" size={18} color={reviewDesk.muted} />
            )}
          </TouchableOpacity>
          <TouchableOpacity
            onPress={(e) => {
              e.stopPropagation?.();
              onOpen(item);
            }}
            style={[
              webStyles.openButton,
              isPhone && webStyles.openButtonTiny,
              isPhone && webStyles.rowActionPhone,
            ]}
            accessibilityRole="button"
          >
            <Text style={webStyles.openButtonText}>{t('common.open')}</Text>
            <MaterialIcons name="chevron-right" size={18} color={reviewDesk.accent} />
          </TouchableOpacity>
        </>
      )}
    </TouchableOpacity>
  );
}

export function LibrarySharedRow({
  item,
  isPhone,
  onOpen,
}: {
  item: SharedDocument;
  isPhone: boolean;
  onOpen: (item: SharedDocument) => void;
}) {
  const { t } = useTranslation();
  return (
    <TouchableOpacity
      style={[
        webStyles.sessionRow,
        isPhone && webStyles.sessionRowTiny,
        isPhone && webStyles.sessionRowPhone,
      ]}
      onPress={() => onOpen(item)}
      accessibilityRole="button"
      testID={`library_shared_${item.share_id}`}
    >
      <View style={webStyles.fileIcon}>
        <MaterialIcons name={itemIcon(item)} size={26} color={reviewDesk.accent} />
      </View>
      <View style={[webStyles.sessionInfo, isPhone && webStyles.sessionInfoPhone]}>
        <Text style={webStyles.sessionTitle} numberOfLines={1}>{item.original_filename}</Text>
        <Text style={webStyles.sessionMeta}>
          {itemMeta(item, t)}
          {item.shared_by?.full_name ? `  ·  ${t('library.sharedBy', { name: item.shared_by.full_name })}` : ''}
        </Text>
      </View>
      <StatusPill item={item} isPhone={isPhone} colored={false} />
      <TouchableOpacity
        onPress={(e) => {
          // The inner button used to swallow the press without opening anything.
          e.stopPropagation?.();
          onOpen(item);
        }}
        style={[
          webStyles.openButton,
          isPhone && webStyles.openButtonTiny,
          isPhone && webStyles.rowActionPhone,
        ]}
        accessibilityRole="button"
      >
        <Text style={webStyles.openButtonText}>{t('common.open')}</Text>
        <MaterialIcons name="chevron-right" size={18} color={reviewDesk.accent} />
      </TouchableOpacity>
    </TouchableOpacity>
  );
}

export function LibraryDeleteDialog({
  item,
  deleting,
  error,
  onCancel,
  onConfirm,
}: {
  item: UserDocumentItem;
  deleting: boolean;
  error: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();
  const failed = isFailedTranscript(item);
  const isAnalysis = item.document_type === 'user_analysis';
  // CHAT-8: interpolate the file name instead of showing "{{filename}}".
  const body = failed
    ? t('library.deleteFailedArtifactCopy')
    : isAnalysis
      ? t('library.deleteWithChat', { filename: item.original_filename })
      : t('library.deleteConfirm', { filename: item.original_filename });

  return (
    <View style={webStyles.deleteOverlay} testID="library_delete_dialog">
      <View style={webStyles.deleteDialog}>
        <View style={webStyles.deleteDialogIcon}>
          <MaterialIcons name={failed ? 'cleaning-services' : 'delete-outline'} size={26} color={COLORS.error} />
        </View>
        <Text style={webStyles.deleteDialogTitle}>
          {failed ? t('library.deleteFailedArtifactTitle') : t('library.deleteTitle')}
        </Text>
        <Text style={webStyles.deleteDialogFilename} numberOfLines={2}>
          {item.original_filename}
        </Text>
        <Text style={webStyles.deleteDialogBody}>{body}</Text>
        {!!error && (
          <View style={webStyles.deleteErrorBox}>
            <MaterialIcons name="error-outline" size={18} color={COLORS.error} />
            <Text style={webStyles.deleteErrorText}>{error}</Text>
          </View>
        )}
        <View style={webStyles.deleteDialogActions}>
          <TouchableOpacity
            style={webStyles.cancelDeleteButton}
            onPress={onCancel}
            disabled={deleting}
            accessibilityRole="button"
          >
            <Text style={webStyles.cancelDeleteText}>{t('common.cancel')}</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={webStyles.confirmDeleteButton}
            onPress={onConfirm}
            disabled={deleting}
            accessibilityRole="button"
          >
            {deleting ? (
              <ActivityIndicator size="small" color="#FFFFFF" />
            ) : (
              <>
                <Text className="text-white" style={webStyles.confirmDeleteText}>
                  {failed ? t('library.deleteFailedArtifactAction') : t('common.delete')}
                </Text>
                <MaterialIcons name="arrow-forward" size={18} color="#FFFFFF" />
              </>
            )}
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
}
